import React, { Component, ReactNode } from 'react';
import { Avatar, Button, Layout, Modal, Select } from 'antd';
import { connect } from 'react-redux';
import formatDate from 'date-fns/format';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Content } from 'antd/lib/layout/layout';
import cx from 'classnames';
import debounce from 'debounce';
import { RouteComponentProps, StaticContext, withRouter } from 'react-router';
import { Link } from 'react-router-dom';

import { ReactComponent as CrossSVG } from '../../../assets/sightx-icons/svg/x-close.svg';
import { ReactComponent as ChevronIconSVG } from '../../../assets/sightx-icons/svg/icon-chevron.svg';
import { ReactComponent as WarningIcon } from '../../../assets/sx-icon-exclamation-point.svg';
import { getActiveView, setActiveView, ViewTypes } from '../../../slices/adminDashboard/view';
import {
  actions as userListActions,
  selectors as userListSelectors,
  UpdateTableQueryPayload
} from '../../../slices/users/list';
import { selectors as authSelectors } from '../../../slices/auth';
import { RootState } from '../../../slices/rootReducer';
import { ISectionsAccessPermissions, IUser } from '../../../slices/users/user';
import { ListParams, ScrollPosition } from '../../../slices/utils';
import { TableSort } from '../../../utils/dataTable/data';
import { isEmpty } from '../../../utils/utils';
import { fetchDropdownOptions } from '../../../slices/filters/sideMenuFilters.thunks';
import { actions as filterMenuActions, selectors as filterMenuSelector } from '../../../slices/filters/sideMenuFilters';
import {
  actions as filterDashboardActions,
  selectors as filterDashboardSelector
} from '../../../slices/filters/dashboardFilters';
import { colors } from '../../../styles/variables';
import { IAccount } from '../../../slices/accounts/account';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { DATES_FORMAT } from '../../../utils/constants';
import { IUpdateUserTypePayload, updateUserTypeById } from '../../../slices/settings/userType.thunk';
import { IUpdateUserStatusPayload, updateUserStatusById } from '../../../slices/settings/userStatus.thunk';
import { TextSearch } from '../../common/textSearch';
import { DataTable } from '../../common/dataTable/dataTable';
import { LightDataTable } from '../../common/dataTable/lightDataTable';
import { UserTypes } from '../../common/domain/userTypes';
import { searchableFields } from '../../users/filters/fields';
import { FilterOperators, Ifilter } from '../../common/filters/interfaces';
import { buildSearchFilters } from '../../common/filters/helper';
import { ProgressBar } from '../../common/progressBar';
import { SxPagination } from '../../common/dataTable/pagination.plgexternal';
import { SxTooltip } from '../../common/utils/wrapComponents';
import { AccessTypes } from '../../common/domain/accountAccessType';
import { SxAlert } from '../../common/sxAlert';
import { ISettingsUser, userColumnsLight, userColumnsLightWithAccessColumn } from './utils';
import { getAvailableUserTypesWithNoFilter, getUserTypeOptionsForPlgExternal } from '../../../utils/user';
import { isIncrowdClient, isNetquestClient, isPureProfileClient } from '../../../utils/clients';
import { LinkButton } from '../../common/linkButton';
import { AddUsersInBulkModal } from '../../users/modal/addUsersInBulkModal';
import { UpgradeTooltipText } from '../../common/accessControl';
import { getUserInitials } from '../../common/utils/string';
import { UserRoles } from '../../common/domain/userRoles';
import icon from '../../../assets/sightx-icons/svg/icon-sightx-featured.svg';
import { parseNumber } from '../../../utils/parseNumber';
import { toMonthDayYearFormat } from '../../accounts/account/utils';
import { AutoEllipsisTooltip } from '../../common/autoEllipsisTooltip';
import { fetchAccountsList } from '../../../slices/accounts/list.thunks';
import { EntityHandlers } from '../../common/dataTable/dataTable.plgexternal';
import { SectionAccessPermissionsModal } from '../../users/modal/sectionAccessPermissionsModal';

import styles from './usersSettings.module.scss';

const initialScroll: Readonly<ScrollPosition> = { x: 0, y: 0 };

enum UserLabelTypes {
  OWNER = 'Owner',
  ADMIN = 'Admin',
  SxADMIN = 'Sx Admin',
  USER = 'User'
}

interface State {
  showAddUsersInBulkModal: boolean;
  currentPage: number;
  pageSize: number;
  tableInitialScroll: ScrollPosition;
  isPurchasingSeats: boolean;
  showDeactivateUserModal: boolean;
  userIdToDeactivate: string | null;
  accountIdToDeactivate: string | null;
  userPermissions?: ISectionsAccessPermissions | null;
  userToEditEmail: string | null;
  openSectionsPermissionsModal: boolean;
}

interface RouteState {
  openAddUsersModal?: boolean;
  successMessage?: boolean;
}

interface StateProps {
  users: IUser[];
  totalCountUsers: number;
  loading: boolean;
  sort: TableSort;
  searchQuery: string;
  loggedUser: IUser | null;
  activeView: string;
  menuFilters: Ifilter[];
  dashboardFilters: Ifilter[];
  wasFilterApplied: boolean;
  account?: IAccount | null;
  isAccountLoading: boolean;
  accountId?: string;
  seatsToRemoveAtEndOfCycle: number;
  usersToDisableAtEndOfCycle: string[];
  cycleEndDate: number;
  isUsersListDirty: boolean;
}

interface DispatchProps {
  fetchUsersList: (params: ListParams<Ifilter[]>) => void;
  updateTableQuery: (payload: UpdateTableQueryPayload) => void;
  setSearchFilters: (payload: Ifilter[]) => void;
  setActiveView: (view: ViewTypes) => void;
  fetchDropdownOptions: () => void;
  setFilterAppliedState: (payload: boolean) => void;
  updateUserTypeById: (params: IUpdateUserTypePayload) => void;
  updateUserStatusById: (params: IUpdateUserStatusPayload) => void;
}

interface MatchProps {
  id: string;
}

interface IOption {
  key: string | number;
  value: string;
}

interface IUserData {
  isMainUser: boolean;
  isLiteAccount: boolean;
  isLoggedUserOwner: boolean;
  isLoggedUserSxAdmin: boolean;
}

type ComponentProps = RouteComponentProps<MatchProps, StaticContext, RouteState> & StateProps & DispatchProps;

const MAXIMUM_SEATS_FOR_PRO_ACCOUNTS = 3;
class UsersSettingDashboardBase extends Component<ComponentProps, State> {
  private debouncedFetchUsersList: (params: ListParams<Ifilter[]>) => void;
  private dataTable: React.RefObject<DataTable<IUser>>;
  constructor(props: ComponentProps) {
    super(props);
    const showDeactivateUserModal = false;
    const userIdToDeactivate = null;
    const accountIdToDeactivate = null;
    this.state = {
      showAddUsersInBulkModal: false,
      currentPage: 1,
      pageSize: 25,
      tableInitialScroll: initialScroll,
      isPurchasingSeats: false,
      showDeactivateUserModal,
      userIdToDeactivate,
      accountIdToDeactivate,
      openSectionsPermissionsModal: false,
      userToEditEmail: null,
      userPermissions: null
    };
    this.onSearch = this.onSearch.bind(this);
    this.debouncedFetchUsersList = debounce(this.props.fetchUsersList, 300);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleOpenModal = this.handleOpenModal.bind(this);
    this.onUserChangePage = this.onUserChangePage.bind(this);
    this.dataTable = React.createRef();
    this.onChangeUserTypeSelection = this.onChangeUserTypeSelection.bind(this);
    this.onDeactivateUser = this.onDeactivateUser.bind(this);
    this.onCloseDeactivateUserModal = this.onCloseDeactivateUserModal.bind(this);
    this.openDeactivateUserModal = this.openDeactivateUserModal.bind(this);
    this.deactivateSelectedUser = this.deactivateSelectedUser.bind(this);
    this.handleEditEntity = this.handleEditEntity.bind(this);
    this.handleCloseSectionPermissionsModal = this.handleCloseSectionPermissionsModal.bind(this);
  }

  componentDidMount(): void {
    this.initFiltersDropdownOptions();
    this.checkActiveView();
    this.onSearch('');
    if (this.props.location.state?.openAddUsersModal) {
      this.setState({
        showAddUsersInBulkModal: true,
        isPurchasingSeats: true
      });
      this.props.location.state = {};
    }
    const { accountId } = this.props;
    if (!accountId) {
      return;
    }
    const fetchAccountsRequest: ListParams<Ifilter[]> = {
      filters: [
        {
          field: '_id',
          operands: [accountId],
          operator: FilterOperators.isAnyOf,
          validateInputs: false
        }
      ]
    };
    fetchAccountsList(fetchAccountsRequest);
  }

  private searchUsers() {
    this.initFiltersDropdownOptions();
    this.checkActiveView();
    this.onSearch(this.props.searchQuery);
  }

  componentDidUpdate(prevProps: StateProps & DispatchProps): void {

    const { isUsersListDirty } = this.props;
    if (isUsersListDirty && !prevProps.isUsersListDirty) {
      this.searchUsers();
      return;
    }

    if (isEmpty(prevProps.users) && !isEmpty(this.props.users)) {
      this.checkFilterAppliedState();
    }
  }

  private checkFilterAppliedState() {
    const { wasFilterApplied, setFilterAppliedState } = this.props;
    if (wasFilterApplied) {
      this.setState({ currentPage: 1 });
      setFilterAppliedState(false);
    }
  }

  private checkActiveView() {
    const { activeView, setActiveView } = this.props;
    if (activeView !== ViewTypes.User) {
      setActiveView(ViewTypes.User);
    }
  }

  private handleOpenModal() {
    this.onToggleShowAddUsersInBulkModal(true);
  }

  private handleCloseModal() {
    this.onToggleShowAddUsersInBulkModal(false);
  }

  private onToggleShowAddUsersInBulkModal(isOpen: boolean) {
    this.setState({
      showAddUsersInBulkModal: isOpen,
      isPurchasingSeats: false
    });
    this.searchUsers();
  }

  private renderDateField = (dateField: Date | string): ReactNode => {
    if (!dateField) {
      return null;
    }
    return <div>{formatDate(new Date(dateField), DATES_FORMAT)}</div>;
  };

  private onChangeUserTypeSelection(value: string, user: IUser) {
    const { accountId = '' } = this.props;
    const request: IUpdateUserTypePayload = {
      userType: value === 'Admin' ? UserTypes.AccountAdmin : UserTypes.User,
      id: user.id,
      accountId, // TODO, accountId should be fetched and validated on the backend please fix with https://sightx.atlassian.net/browse/SIG-13321
      currentPage: this.state.currentPage
    };
    this.props.updateUserTypeById(request);
  }

  private onDeactivateUser(userId: string, accountId: string) {
    const request: IUpdateUserStatusPayload = {
      isActive: false,
      id: userId,
      accountId: accountId,
      currentPage: this.state.currentPage
    };
    this.props.updateUserStatusById(request);
  }

  private getUserTypeForUser(user: IUser) {
    switch (user.userType) {
    case UserTypes.SxAdmin:
      return UserLabelTypes.SxADMIN;
    case UserTypes.AccountAdmin:
      return UserLabelTypes.ADMIN;
    case UserTypes.User:
      return UserLabelTypes.USER;
    default:
      return UserLabelTypes.USER;
    }
  }

  private renderSelector(selector: JSX.Element, userId: string): JSX.Element {
    const { usersToDisableAtEndOfCycle, cycleEndDate } = this.props;
    if (usersToDisableAtEndOfCycle.includes(userId)) {
      return <div className={styles.selectorContainer}>
        <div className={styles.selector}>
          {selector}
        </div>
        <div className={styles.removeAlert}>
          <AutoEllipsisTooltip
            text={`Active until ${toMonthDayYearFormat(new Date(cycleEndDate * 1000))}`}
          />
        </div>
      </div>;
    }
    return selector;
  }

  private generateUserCard = (user: IUser) => {
    const userInitials = getUserInitials(user);
    const avatar = user.avatar ? <Avatar src={user.avatar} /> :
      <Avatar className={styles.profileButton}>
        {userInitials}
      </Avatar>;
    return <div className={styles.userContainer}>
      {avatar}
      <div className={styles.userName}>
        <span className={styles.name}>{user.firstName} {user.lastName}</span>  <br />
        <span className={styles.email}>{user.email}</span>
      </div>
    </div>;
  }

  private generateUserTypeList(user: IUser) {
    const { loggedUser, account } = this.props;
    const userTypeForCurrentUser = this.getUserTypeForUser(user);

    const selectedRoles = isIncrowdClient() ?
      getAvailableUserTypesWithNoFilter({ loggedUser, editingUser: user }) :
      getUserTypeOptionsForPlgExternal({ loggedUser, editingUser: user, accountName: account?.name });

    const roleOptions: IOption[] = selectedRoles.map((role) => {
      let valueForOptions = role.label === 'Account Admin' ? UserLabelTypes.ADMIN : role.label;
      valueForOptions = role.label === 'SightX Admin' ? UserLabelTypes.SxADMIN : valueForOptions;
      return {
        key: role.key,
        value: valueForOptions
      };
    });

    return <Select
      suffixIcon={<Icon component={ChevronIconSVG} />}
      showArrow
      placeholder='Please select'
      options={roleOptions}
      value={userTypeForCurrentUser}
      onChange={(value) => this.onChangeUserTypeSelection(value, user)}
      className={styles.customDropdown}
    />;
  }

  private getUserData(userData: IUserData): ISettingsUser[] {
    const {
      users,
      loggedUser
    } = this.props;

    const { isMainUser, isLiteAccount, isLoggedUserOwner, isLoggedUserSxAdmin } = userData;
    return users.reduce((usersList: ISettingsUser[], user: IUser): ISettingsUser[] => {
      if (user && loggedUser && !user.isActive) {
        return usersList;
      }
      const userName = this.generateUserCard(user);
      const lastLoginDate = this.renderDateField(user.lastLogin ?? '');

      if (!isMainUser || isLiteAccount) {
        usersList.push({
          id: user.id,
          user: userName,
          userType: this.getUserTypeForUser(user),
          lastLogin: lastLoginDate,
          displayActionMenu: false,
          accountId: user.accountId,
          sectionPermissions: user.sectionsAccessPermissions,
          email: user.email
        });
        return usersList;
      }

      const userTypeForCurrentUser = this.getUserTypeForUser(user);

      if (user.id === loggedUser?.id ||
        (userTypeForCurrentUser === UserLabelTypes.SxADMIN && (!isLoggedUserOwner || !isLoggedUserSxAdmin)) ||
        (userTypeForCurrentUser === UserLabelTypes.ADMIN && (!isMainUser))
      ) {
        usersList.push({
          id: user.id,
          user: userName,
          userType: userTypeForCurrentUser,
          lastLogin: lastLoginDate,
          displayActionMenu: false,
          accountId: user.accountId,
          sectionPermissions: user.sectionsAccessPermissions,
          email: user.email
        });
        return usersList;
      }

      const selectorComponent = this.generateUserTypeList(user);
      const userTypeDropdown = this.renderSelector(selectorComponent, user.id);

      usersList.push({
        id: user.id,
        user: userName,
        userType: userTypeDropdown,
        lastLogin: lastLoginDate,
        displayActionMenu: true,
        accountId: user.accountId,
        sectionPermissions: user.sectionsAccessPermissions,
        email: user.email
      });

      return usersList;
    }, []);
  }

  private renderTable(): ReactNode {
    const {
      loading,
      sort,
      loggedUser,
      account
    } = this.props;
    const isLoggedUserOwner = (loggedUser?.roles ?? []).find((role) => role === UserRoles.CompanyOwner);
    const isLoggedUserSxAdmin = loggedUser?.userType === UserTypes.SxAdmin;
    const isLoggedUserAccountAdmin = loggedUser?.userType === UserTypes.AccountAdmin;
    const isMainUser = isLoggedUserSxAdmin || isLoggedUserAccountAdmin || !!isLoggedUserOwner;
    const isLiteAccount = account?.accessType === AccessTypes.Lite;
    const usersData: ISettingsUser[] = this.getUserData({ isMainUser, isLiteAccount, isLoggedUserOwner: !!isLoggedUserOwner, isLoggedUserSxAdmin });
    const columns = isLoggedUserAccountAdmin ? userColumnsLightWithAccessColumn : userColumnsLight;
    return <LightDataTable<ISettingsUser>
      loggedInUser={loggedUser}
      columns={columns}
      withActiveToggle
      data={usersData || []}
      loading={loading}
      sort={sort}
      initialScroll={this.state.tableInitialScroll}
      onDeactivateUser={this.openDeactivateUserModal}
      toggleEditEntityHandler={this.handleEditEntity}
    />;
  }

  private renderAddUserButton() {
    const { loggedUser, account } = this.props;
    const isNotAnAllowedAccountType = account?.accessType === AccessTypes.Lite;
    if (account === null || isNotAnAllowedAccountType ||
      !loggedUser || ![UserTypes.SxAdmin, UserTypes.AccountAdmin].includes(loggedUser.userType)) {
      return null;
    }
    const seatsPurchased = account?.seatsPurchased ?? 0;
    const seatsUsed = account?.seatsUsed ?? 0;
    const areSeatsAvailable = seatsUsed < seatsPurchased;
    const isDisabled = !areSeatsAvailable;

    return (
      <SxTooltip
        Component={
          this.renderAddUsersButton(isDisabled)
        }
        hideTooltip={!isDisabled}
        tooltipText={<UpgradeTooltipText />}
      />
    );
  }

  private renderAddUsersButton(isDisabled: boolean) {
    if (isNetquestClient() || isPureProfileClient()) {
      return <span>
        {this.renderAccessPermissionsModal()}
      </span>;
    }
    return <span>
      <LinkButton
        disabled={isDisabled}
        onClick={this.handleOpenModal}
        type="primary"
      >
        Assign users
      </LinkButton>
      {this.renderAddUsersInBulkModal()}
      {this.renderAccessPermissionsModal()}
    </span>;
  }

  private initFiltersDropdownOptions(): void {
    const { fetchDropdownOptions } = this.props;
    fetchDropdownOptions();
  }

  private onSearch(query = ''): void {
    const { updateTableQuery, setSearchFilters, menuFilters, sort } = this.props;
    const { pageSize } = this.state;
    this.dataTable.current?.resetSelectedtRowKeys();
    this.setState({ currentPage: 1, tableInitialScroll: initialScroll });
    const search = buildSearchFilters(query, searchableFields);
    const request: ListParams<Ifilter[]> = {
      search,
      filters: menuFilters,
      pagination: { offset: 0, limit: pageSize },
      sort
    };
    updateTableQuery(query);
    setSearchFilters(search);
    this.debouncedFetchUsersList(request);
  }

  private onUserChangePage(pageNumber: number, numberOfRows: number): void {
    const { menuFilters, dashboardFilters, sort } = this.props;
    this.setState({ currentPage: pageNumber, pageSize: numberOfRows, tableInitialScroll: initialScroll });
    const offset = (pageNumber - 1) * numberOfRows;
    const request: ListParams<Ifilter[]> = {
      filters: menuFilters,
      search: dashboardFilters,
      pagination: { offset: offset, limit: numberOfRows },
      sort
    };
    this.debouncedFetchUsersList(request);
  }

  private handleEditEntity(isOpen: boolean, user: ISettingsUser, handler: EntityHandlers | null) {
    if (handler === EntityHandlers.AccessPermissions) {
      this.setState({
        userIdToDeactivate: user.id,
        userToEditEmail: user.email,
        userPermissions: user.sectionPermissions,
        openSectionsPermissionsModal: isOpen
      });
    }
  }

  private handleCloseSectionPermissionsModal() {
    this.setState({
      userIdToDeactivate: null,
      userPermissions: null,
      userToEditEmail: null,
      openSectionsPermissionsModal: false
    });
  }

  private renderBanner() {
    const { account, seatsToRemoveAtEndOfCycle } = this.props;

    const seatsPurchased = account?.seatsPurchased ?? 0;
    const seatsUsed = account?.seatsUsed ?? 0;
    const accountType = account?.accessType ?? '';

    const seatsAlreadyUsed = seatsUsed === seatsPurchased;
    const isLiteAccount = accountType === AccessTypes.Lite;
    const isEnterpriseAccount = accountType === AccessTypes.Enterprise;
    const isProfessionalAccount = accountType === AccessTypes.Professional;
    const canBuyMoreLicenses = seatsAlreadyUsed && seatsPurchased < MAXIMUM_SEATS_FOR_PRO_ACCOUNTS;
    const blockedByRemoval = seatsToRemoveAtEndOfCycle > 0;

    if (isLiteAccount) {
      return <SxAlert title={
        <div className={styles.titleContainer}>
          <span className={styles.title}>Your Lite plan is limited to one user</span> <Link to="/settings/subscription">Upgrade to a paid plan</Link> to add users and collaborate with your team!
        </div>
      } />;
    }
    if (!blockedByRemoval && ((seatsAlreadyUsed && isEnterpriseAccount) || (seatsAlreadyUsed && canBuyMoreLicenses && isProfessionalAccount))) {
      return <SxAlert title={
        <div className={styles.titleContainer}>
          <span className={styles.title}>You're using all of the paid seats in your account.</span> <Link to="/settings/subscription">Purchase additional licenses</Link> to add more users.
        </div>
      } />;
    }
    if (seatsAlreadyUsed && !canBuyMoreLicenses && isProfessionalAccount) {
      return <SxAlert title={
        <div className={styles.titleContainer}>
          <span className={styles.title}>You've reached the limit of users in your account.</span> <Link to="/settings/subscription">Upgrade to an Enterprise plan</Link> to add more users.
        </div>
      } />;
    }
    return null;

  }

  private renderPagination(): JSX.Element {
    const { totalCountUsers } = this.props;
    const { currentPage, pageSize } = this.state;
    return <SxPagination
      onChangePage={this.onUserChangePage}
      paginationTable={{ currentPage, pageSize, totalCount: totalCountUsers }}
    />;
  }

  private renderAddUsersInBulkModal() {
    const {
      showAddUsersInBulkModal,
      isPurchasingSeats
    } = this.state;
    const { account } = this.props;
    const purchasedSeats = account?.seatsPurchased ?? 0;
    const seatsUsed = account?.seatsUsed ?? 0;
    const availableSeats = purchasedSeats - seatsUsed;
    if (!showAddUsersInBulkModal || !account || availableSeats <= 0) {
      return null;
    }
    return (
      <AddUsersInBulkModal
        onClose={this.handleCloseModal}
        availableSeats={availableSeats}
        accountId={account.id}
        isForSxAdmin={false}
        accountType={account.accessType}
        isPurchasingSeats={isPurchasingSeats}
      />
    );
  }

  private renderAccessPermissionsModal() {
    const { userToEditEmail, openSectionsPermissionsModal, userPermissions, userIdToDeactivate } = this.state;
    if (!userIdToDeactivate || !userToEditEmail || !userPermissions) {
      return null;
    }
    return <SectionAccessPermissionsModal
      onClose={this.handleCloseSectionPermissionsModal}
      permissions={userPermissions}
      userEmail={userToEditEmail}
      userId={userIdToDeactivate}
      visible={openSectionsPermissionsModal}
    />;
  }

  private openDeactivateUserModal(userIdToDeactivate: string, accountIdToDeactivate: string) {
    this.setState({
      showDeactivateUserModal: true,
      userIdToDeactivate,
      accountIdToDeactivate
    });
  }

  private onCloseDeactivateUserModal() {
    this.setState({
      userIdToDeactivate: null,
      accountIdToDeactivate: null,
      showDeactivateUserModal: false
    });
  }

  private deactivateSelectedUser() {
    const { accountIdToDeactivate, userIdToDeactivate } = this.state;
    const { updateUserStatusById } = this.props;
    if (!accountIdToDeactivate || !userIdToDeactivate) {
      return null;
    }
    updateUserStatusById({
      accountId: accountIdToDeactivate,
      id: userIdToDeactivate,
      isActive: false
    });
    this.setState({
      accountIdToDeactivate: null,
      userIdToDeactivate: null,
      showDeactivateUserModal: false
    });
  }

  private renderDeactivateUserModal() {
    const { showDeactivateUserModal } = this.state;

    return (
      <Modal
        visible={showDeactivateUserModal}
        footer={null}
        onCancel={this.onCloseDeactivateUserModal}
        className={styles.deactivateUserModal}
        closeIcon={<Icon className={styles.closeIcon} component={CrossSVG} />}
        centered
        maskClosable={false}
        width={400}
      >
        <div className={styles.icon}>
          <Icon component={WarningIcon} />
        </div>
        <div className={styles.header}>
          Deactivate User
        </div>
        <div className={styles.content}>
          Are you sure you want to deactivate this user? They will immediately lose access to the SightX platform and all of their projects.
        </div>
        <div className={styles.footer}>
          <Button
            type='ghost'
            onClick={this.onCloseDeactivateUserModal}
          >
            Cancel
          </Button>
          <Button
            type='primary'
            onClick={this.deactivateSelectedUser}
          >
            Deactivate User
          </Button>
        </div>
      </Modal>
    );
  }

  private renderSeatsAndUser() {
    const { account, seatsToRemoveAtEndOfCycle } = this.props;
    const seatsPurchased = account?.seatsPurchased ?? 0;
    const seatsUsed = account?.seatsUsed ?? 0;
    const percent = seatsPurchased ? (100 * seatsUsed) / seatsPurchased : 100;
    const seatsToRemove = parseNumber(seatsToRemoveAtEndOfCycle);
    const seatsLabel = (seatsToRemoveAtEndOfCycle === 0 || seatsToRemoveAtEndOfCycle === 1) ? 'seat' : 'seats';
    if (isNetquestClient()) {
      return <span>{`${seatsUsed} user${seatsUsed === 1 ? '' : 's'} created`}</span>;
    }
    return <>
      <ProgressBar
        percent={percent > 100 ? 100 : percent}
        showInfo={false}
        strokeColor={seatsUsed === seatsPurchased ? colors.green700 : colors.sightxPurple400}
        trailColor={colors.gray200}
        className={styles.progress}
      />
      <span>{`${seatsUsed} of ${seatsPurchased} seats used`}</span>
      {seatsToRemoveAtEndOfCycle > 0 && <span className={styles.removalAlert}>
        {`${seatsToRemove} additional ${seatsLabel} expiring at the end of cycle`}
      </span>}
    </>;
  }

  render(): ReactNode {
    const { account, isAccountLoading } = this.props;
    const accountType = account?.accessType ?? '';
    if (isAccountLoading) {
      return <div className={styles.loadingContainer}>
        <LoadingOutlined />
      </div>;
    }

    return <div className={styles.container}>
      {this.renderBanner()}
      {this.renderDeactivateUserModal()}
      <div className={styles.dashboardForDevelopment}>
        <Layout
          className={styles.filterDrawerContainer}
        >
          <Content className={styles.dashboardContent}>
            <section className={styles.mainContent}>
              <div className={styles.actionsHeader}>
                <div className={styles.leftSection}>
                  <div className={styles.seats}>
                    <div className={styles.logo}>
                      <img src={icon} alt="sightXicon" className={styles.logoIcon} />
                    </div>
                    {this.renderSeatsAndUser()}
                  </div>
                </div>
                <div className={styles.rightSection}>
                  <div className={styles.textSearchContainer}>
                    {accountType !== AccessTypes.Lite && <TextSearch onSearch={this.onSearch} searchQuery={this.props.searchQuery} />}
                  </div>
                  {this.renderAddUserButton()}
                </div>
              </div>
              <div className={cx(styles.tableContainer, { [styles.loading]: this.props.loading })}>
                {this.renderTable()}
              </div>
            </section>
            <div className={styles.paginationContainer}>
              {this.renderPagination()}
            </div>
          </Content>
        </Layout>
      </div>
    </div>;
  }
}

const mapStateToProps = (state: RootState): StateProps => {
  const user = authSelectors.getLoggedInUser(state);
  const accountId = user?.accountId ?? '';
  const currentFilters = [
    {
      field: 'accountId',
      operands: [accountId],
      operator: FilterOperators.isAnyOf,
      validateInputs: false
    }
  ];

  const account = accountListSelectors.getAccountById(state, accountId);
  return {
    users: userListSelectors.getList(state),
    totalCountUsers: userListSelectors.getTotalCount(state),
    loading: userListSelectors.getLoading(state),
    sort: filterDashboardSelector.getSorter(state),
    searchQuery: filterDashboardSelector.getSearchQuery(state),
    loggedUser: authSelectors.getLoggedInUser(state),
    activeView: getActiveView(state),
    menuFilters: currentFilters,
    dashboardFilters: filterDashboardSelector.getSearchFilters(state),
    wasFilterApplied: filterMenuSelector.getFilterAppliedState(state),
    account,
    isAccountLoading: accountListSelectors.getLoading(state),
    accountId: user?.accountId,
    seatsToRemoveAtEndOfCycle: account?.subscription?.seatsToRemoveAtEndOfCycle ?? 0,
    usersToDisableAtEndOfCycle: account?.subscription?.usersToDisableAtEndOfCycle ?? [],
    cycleEndDate: account?.subscription?.cycleEndDate ?? 0,
    isUsersListDirty: userListSelectors.getIsUserListDirty(state)
  };
};

const dispatchToProd: DispatchProps = {
  fetchUsersList: userListActions.fetchUsersList,
  updateTableQuery: filterDashboardActions.setSearchQuery,
  setSearchFilters: filterDashboardActions.setSearchFilters,
  setFilterAppliedState: filterMenuActions.setFilterAppliedState,
  fetchDropdownOptions,
  setActiveView,
  updateUserTypeById,
  updateUserStatusById
};

const ConnectedUsersDashboard = connect(mapStateToProps, dispatchToProd)(UsersSettingDashboardBase);

export const UsersSettingDashboard = withRouter(ConnectedUsersDashboard);

